import { getIn } from "formik";
import { isPaidTrainingTimelineLocked } from "../../../../../utils/companyPortal/eventEditRules";

export {
  SHIFT_TYPE_VALUES,
  getShiftOptions,
  getShiftOptionLabel,
  hasAvailableShiftType,
} from "../../../../../utils/eventShiftOptions";

export const createShift = () => ({
  portal_organizer_checked_in: false,
  shift_type: "",
  starts_at: "",
  ends_at: "",
  payroll_amount: "",
  payroll_per_day: "",
  before_check_in_margin: "",
  after_check_in_margin: "",
  before_check_out_margin: "",
  after_check_out_margin: "",
  training_attributes: null,
});

export const createTraining = () => ({
  name: "",
  location_address: "",
  lat: null,
  lng: null,
  location_radius: null,
  payroll_amount: "",
  number_of_days: "",
  description: "",
  starts_at: "",
  ends_at: "",
  before_check_in_margin: "",
  after_check_in_margin: "",
  before_check_out_margin: "",
  after_check_out_margin: "",
});

/** First non-empty boundary (meters) for inheriting event map radius into paid training */
export const firstTruthyBoundaryMeterString = (...candidates) => {
  for (const v of candidates) {
    if (v == null || v === "") continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
};

export const createRole = () => ({
  role_id: "",
  role_name: "",
  assigned_applicants_count: null,
  maximum_number_of_males: "",
  maximum_number_of_females: "",
  uniform_code: "",
  event_role_shifts_attributes: [createShift()],
});

/** Yup/Formik may store a string, nested object, or array at a path — only strings are safe for MUI helperText. */
const firstStringError = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const s = firstStringError(item);
      if (s) return s;
    }
    return "";
  }
  if (typeof value === "object") {
    for (const nested of Object.values(value)) {
      const s = firstStringError(nested);
      if (s) return s;
    }
  }
  return "";
};

export const getVisibleError = (formik, name) => {
  if (!formik) return "";
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  if (!(touched || formik.submitCount > 0)) return "";
  return firstStringError(error);
};

/** Company-portal edit locks for one shift (organizer checked in / training timeline started). */
export const getShiftEditLocks = ({
  formik,
  shift,
  shiftBaseName,
  portalCompanyOngoingEdit,
  companyPortalMode,
  isEditMode,
}) => {
  const organizerShiftLocked = Boolean(
    portalCompanyOngoingEdit && shift?.portal_organizer_checked_in,
  );
  const trainingScheduleLocked = Boolean(
    companyPortalMode &&
      isEditMode &&
      shift?.training_attributes?.id &&
      !organizerShiftLocked &&
      isPaidTrainingTimelineLocked(
        getIn(formik.initialValues, `${shiftBaseName}.training_attributes.starts_at`),
      ),
  );
  return { organizerShiftLocked, trainingScheduleLocked };
};

export const getRoleLabel = (role, rolesOptions, labels, index) => {
  const selectedRole = rolesOptions.find(
    (option) => String(option.id) === String(role.role_id),
  );

  return (
    selectedRole?.name ||
    role.role_name ||
    `${labels.roleFallback} ${index + 1}`
  );
};

export const getRoleInitials = (index) => {
  return `R${index + 1}`;
};

export const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const formatCurrency = (value) =>
  `SAR ${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const getEventDayCount = (event) => {
  const start =
    event?.event_start_at || event?.actualStart || event?.actual_start_at;
  const end = event?.event_end_at || event?.actualEnd || event?.actual_end_at;

  if (!start || !end) return 0;

  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate.getTime() - startDate.getTime();

  if (!Number.isFinite(diff) || diff <= 0) return 1;

  return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const buildTouchedRoles = (roles) => ({
  event_roles_attributes: roles.map((role) => ({
    role_id: true,
    role_name: true,
    maximum_number_of_males: true,
    maximum_number_of_females: true,
    uniform_code: true,
    event_role_shifts_attributes: role.event_role_shifts_attributes.map(
      (shift) => ({
        shift_type: true,
        starts_at: true,
        ends_at: true,
        payroll_amount: true,
        payroll_per_day: true,
        before_check_in_margin: true,
        after_check_in_margin: true,
        before_check_out_margin: true,
        after_check_out_margin: true,
        training_attributes: shift.training_attributes
          ? {
              name: true,
              location_address: true,
              lat: true,
              lng: true,
              location_radius: true,
              payroll_amount: true,
              number_of_days: true,
              description: true,
              starts_at: true,
              ends_at: true,
              before_check_in_margin: true,
              after_check_in_margin: true,
              before_check_out_margin: true,
              after_check_out_margin: true,
            }
          : undefined,
      }),
    ),
  })),
});

